import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Layout from '../components/Layout';
import DesignCardPreview from '../components/DesignCardPreview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft,
  CreditCard, Package, Palette, Eye, Edit, ShoppingBag, Check, Printer, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Build a DesignCardPreview-compatible object from cart item's design + template
const buildPreviewData = (item) => {
  const d = item.design || {};
  const t = item.template || {};
  return {
    front_text_elements: d.front_text_elements || [],
    back_text_elements: d.back_text_elements || [],
    user_logo: d.user_logo,
    template_data: {
      front_bg_color: t.front_bg_color || t.background_color || '#1e293b',
      back_bg_color: t.back_bg_color || '#1e293b',
      front_bg_image: t.front_bg_image,
      back_bg_image: t.back_bg_image,
      front_logo_position: t.front_logo_position,
      back_logo_position: t.back_logo_position,
      back_qr_position: t.back_qr_position,
    },
  };
};

const Cart = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { loadCart: refreshGlobalCart } = useCart();
  
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [previewItem, setPreviewItem] = useState(null);
  const [previewSide, setPreviewSide] = useState('front');
  const [phone, setPhone] = useState('');
  const [phoneSaved, setPhoneSaved] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/shop'); return; }
    loadCart();
    loadUserProfile();
  }, [user, navigate]);

  const loadUserProfile = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const profile = await response.json();
        if (profile.phoneNumber) {
          setPhone(profile.phoneNumber);
          setPhoneSaved(true);
        }
      }
    } catch (error) { console.error('Error loading profile:', error); }
  };

  const savePhoneToProfile = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length !== 10 || phoneSaved) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber })
      });
      if (response.ok) {
        setPhoneSaved(true);
        console.log('Phone saved to profile');
      }
    } catch (error) { console.error('Error saving phone to profile:', error); }
  };

  const loadCart = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCart(data);
        // Sync with global cart context for header update
        refreshGlobalCart();
      }
    } catch (error) { console.error('Error loading cart:', error); }
    finally { setLoading(false); }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) { removeItem(itemId); return; }
    setUpdating(prev => ({ ...prev, [itemId]: true }));
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart/update/${itemId}?quantity=${newQuantity}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await loadCart();
      }
      else { const error = await response.json(); throw new Error(error.detail || 'Failed to update'); }
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to update quantity", variant: "destructive" });
    } finally { setUpdating(prev => ({ ...prev, [itemId]: false })); }
  };

  const removeItem = async (itemId) => {
    setUpdating(prev => ({ ...prev, [itemId]: true }));
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart/remove/${itemId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { 
        toast({ title: "Removed", description: "Item removed from cart" }); 
        await loadCart();
      }
      else { const error = await response.json(); throw new Error(error.detail || 'Failed to remove'); }
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to remove item", variant: "destructive" });
    } finally { setUpdating(prev => ({ ...prev, [itemId]: false })); }
  };

  const handleEditDesign = (item) => {
    // Navigate to edit screen with design_id
    if (item.design?.id && item.design?.template_id) {
      // Get template info to build the URL
      const templateId = item.design.template_id;
      navigate(`/shop/template/${templateId}?design_id=${item.design.id}`);
    } else if (item.design?.id) {
      // Fallback: use design ID in query
      navigate(`/shop?design_id=${item.design.id}`);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-6 md:py-8 px-3 sm:px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="cart-title">Shopping Cart</h1>
              <p className="text-sm sm:text-base text-gray-600">{cart.item_count} item(s) in your cart</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/shop')} className="w-full sm:w-auto text-sm" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />Continue Shopping
            </Button>
          </div>

          {cart.items.length === 0 ? (
            <Card>
              <CardContent className="py-12 sm:py-16 text-center px-4">
                <ShoppingCart className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                <h2 className="text-lg sm:text-xl font-medium text-gray-700 mb-2">Your cart is empty</h2>
                <p className="text-sm sm:text-base text-gray-500 mb-6">Start shopping to add items to your cart</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate('/shop')} variant="outline" className="text-sm sm:text-base">
                    <Palette className="w-4 h-4 mr-2" />Custom Designs
                  </Button>
                  <Button onClick={() => navigate('/shop#ready-made')} className="bg-amber-500 hover:bg-amber-600 text-sm sm:text-base">
                    <ShoppingBag className="w-4 h-4 mr-2" />Ready Made Cards
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col lg:grid lg:grid-cols-5 gap-4 sm:gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-3 space-y-3 sm:space-y-4 order-2 lg:order-1">
                {cart.items.map((item) => (
                  <Card key={item.id} data-testid={`cart-item-${item.id}`}>
                    <CardContent className="p-3 sm:p-4">
                      {/* Full-width design preview for custom designs */}
                      {item.item_type === 'custom_design' && item.design && (
                        <div className="mb-3">
                          <div className="rounded-lg overflow-hidden border shadow-sm cursor-pointer hover:shadow-md transition-shadow max-w-lg"
                            onClick={() => { setPreviewItem(item); setPreviewSide('front'); }}
                            data-testid={`cart-design-preview-${item.id}`}>
                            <DesignCardPreview design={buildPreviewData(item)} side="front" />
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <button className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                              onClick={() => { setPreviewItem(item); setPreviewSide('front'); }}>
                              <Eye className="w-3 h-3" />Preview
                            </button>
                            <button className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              onClick={() => handleEditDesign(item)}
                              data-testid={`cart-edit-design-${item.id}`}>
                              <Edit className="w-3 h-3" />Edit Design
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-3 sm:gap-4">
                        {/* Product Image - for regular products and uploaded designs */}
                        {item.item_type !== 'custom_design' && (
                          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            {item.item_type === 'uploaded_design' && item.uploaded_images?.length > 0 ? (
                              <img src={resolveImageUrl(item.uploaded_images[0].url || `/api/shop/files/${item.uploaded_images[0].file_id}`)} alt="Uploaded Design" className="w-full h-full object-cover" />
                            ) : item.product?.images?.[0] ? (
                              <img src={resolveImageUrl(item.product.images[0])} alt={item.product.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {item.product?.name || 'Product'}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">
                            {item.product?.category}
                            {item.item_type === 'custom_design' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                                <Palette className="w-3 h-3 mr-0.5" />Custom Design
                              </span>
                            )}
                            {item.item_type === 'uploaded_design' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-violet-100 text-violet-800">
                                <Printer className="w-3 h-3 mr-0.5" />Uploaded Design
                              </span>
                            )}
                            {item.order_type === 'nfc_only' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                NFC Only
                              </span>
                            )}
                            {item.order_type === 'paper_only' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800">
                                Paper Only ({item.paper_quantity} cards)
                              </span>
                            )}
                            {item.order_type === 'nfc_paper' && item.paper_quantity && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-violet-100 text-violet-800">
                                + {item.paper_quantity} Paper Cards
                              </span>
                            )}
                          </p>
                          
                          {/* Show pricing breakdown */}
                          {item.pricing && (
                            <div className="text-xs text-gray-600 mb-1 space-y-0.5">
                              {item.order_type === 'nfc_paper' && (
                                <>
                                  <p>NFC ({item.quantity}x): ₹{item.pricing.nfc_price?.toLocaleString()}</p>
                                  <p>Paper ({item.total_paper_cards?.toLocaleString() || (item.paper_quantity * item.quantity)} cards): ₹{item.pricing.paper_price?.toLocaleString()}</p>
                                </>
                              )}
                              {item.order_type === 'paper_only' && (
                                <p>Paper Cards ({item.total_paper_cards?.toLocaleString() || (item.paper_quantity * item.quantity)} cards): ₹{item.pricing.paper_price?.toLocaleString()}</p>
                              )}
                            </div>
                          )}
                          
                          {/* Show discount info for all item types */}
                          {item.pricing?.discount_percent > 0 && (
                            <p className="text-xs text-green-600 mb-1">
                              Bulk discount: {item.pricing.discount_percent}% off (-₹{item.pricing.discount_amount?.toLocaleString()})
                            </p>
                          )}
                          
                          {/* Price */}
                          <div className="flex items-center gap-2 mb-2 sm:mb-3">
                            {item.product?.discount_percent > 0 ? (
                              <>
                                <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                  {formatPrice(item.product.discounted_price || item.product.price * (1 - item.product.discount_percent/100))}
                                </span>
                                <span className="text-xs sm:text-sm text-gray-500 line-through">{formatPrice(item.product.price)}</span>
                              </>
                            ) : (
                              <span className="font-semibold text-gray-900 text-sm sm:text-base">{formatPrice(item.product?.price || 0)}</span>
                            )}
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:justify-between">
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={updating[item.id]}>
                                <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  if (val > 0 && val !== item.quantity) {
                                    updateQuantity(item.id, val);
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  if (val < 1) updateQuantity(item.id, 1);
                                }}
                                className="w-16 h-8 text-center font-medium text-sm px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                disabled={updating[item.id]}
                                data-testid={`quantity-input-${item.id}`}
                              />
                              <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)} disabled={updating[item.id]}>
                                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                              </Button>
                            </div>
                            <div className="flex items-center justify-between xs:justify-end gap-3 sm:gap-4">
                              <span className="font-semibold text-amber-600 text-sm sm:text-base">{formatPrice(item.item_total)}</span>
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                onClick={() => removeItem(item.id)} disabled={updating[item.id]}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <Card className="lg:sticky lg:top-4">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-base sm:text-lg">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 sm:space-y-4">
                    {/* Show cart-level bulk discount badge */}
                    {cart.cart_discount_percent > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center" data-testid="bulk-discount-badge">
                        <p className="text-green-700 font-semibold text-sm" data-testid="discount-percent-text">
                          🎉 {cart.cart_discount_percent}% Bulk Discount Applied!
                        </p>
                        <p className="text-green-600 text-xs mt-1" data-testid="total-cards-text">
                          Based on {cart.item_count} total cards in cart
                        </p>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Cards</span>
                      <span className="font-medium" data-testid="cart-item-count">{cart.item_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal (after discount)</span>
                      <span className="font-medium">{formatPrice(cart.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className="font-medium text-green-600">Free</span>
                    </div>
                    <div className="border-t pt-3 sm:pt-4">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-900">Total</span>
                        <span className="font-bold text-lg sm:text-xl text-amber-600">{formatPrice(cart.total)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Including all taxes</p>
                    </div>

                    {/* ── What you'll receive ── */}
                    {cart.items.length > 0 && (() => {
                      // Calculate quantities by order type
                      let totalNfcCards = 0;
                      let totalPaperCards = 0;
                      
                      cart.items.forEach(item => {
                        const qty = item.quantity || 1;
                        const orderType = item.order_type || 'nfc_paper';
                        const paperQty = item.paper_quantity || 100;
                        
                        if (item.item_type === 'custom_design' || item.item_type === 'uploaded_design') {
                          if (orderType === 'nfc_only') {
                            totalNfcCards += qty;
                          } else if (orderType === 'paper_only') {
                            // Paper only: paper_quantity × quantity
                            totalPaperCards += paperQty * qty;
                          } else { // nfc_paper
                            totalNfcCards += qty;
                            // Paper cards multiply: paper_quantity × quantity
                            totalPaperCards += paperQty * qty;
                          }
                        } else {
                          // Ready-made products
                          totalNfcCards += qty;
                          totalPaperCards += qty * 100;
                        }
                      });
                      
                      return (
                        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="what-youll-receive">
                          <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                            <Package className="w-3.5 h-3.5" />
                            What you'll receive
                          </p>
                          {totalNfcCards > 0 && (
                            <div className="flex items-start gap-2">
                              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-800" data-testid="nfc-cards-qty">
                                  {totalNfcCards} NFC Card{totalNfcCards > 1 ? 's' : ''} with QR
                                </p>
                                <p className="text-[10px] text-gray-500">Tap to share your digital profile</p>
                              </div>
                            </div>
                          )}
                          {totalPaperCards > 0 && (
                            <div className="flex items-start gap-2">
                              <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-800" data-testid="printed-cards-qty">
                                  {totalPaperCards.toLocaleString()} Printed Visiting Cards
                                </p>
                                <p className="text-[10px] text-gray-500">Premium matte finish paper cards</p>
                              </div>
                            </div>
                          )}
                          <div className="pt-1 border-t border-emerald-200 flex items-center gap-1.5 text-[10px] text-emerald-700">
                            <Printer className="w-3 h-3" />
                            <span>All items shipped together · Free delivery</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Phone Number */}
                    <div className="pt-3 border-t">
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4" />
                        Phone Number *
                        {phoneSaved && (
                          <span className="text-xs text-green-600 font-normal ml-auto flex items-center gap-1">
                            <Check className="w-3 h-3" /> Saved
                          </span>
                        )}
                      </label>
                      <Input
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        value={phone}
                        onChange={e => {
                          const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setPhone(newPhone);
                          if (newPhone.length === 10 && !phoneSaved) {
                            setPhoneSaved(false); // Reset saved state if phone changed
                          }
                        }}
                        className="h-10"
                        data-testid="phone-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Required for order updates & delivery</p>
                    </div>

                    <Button 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm sm:text-base" 
                      onClick={async () => {
                        if (!phone || phone.length !== 10) {
                          toast({ title: 'Phone Required', description: 'Please enter a valid 10-digit phone number', variant: 'destructive' });
                          return;
                        }
                        // Save phone to profile if not already saved
                        if (!phoneSaved) {
                          await savePhoneToProfile(phone);
                        }
                        sessionStorage.setItem('checkout_phone', phone);
                        navigate('/checkout');
                      }} 
                      data-testid="checkout-btn"
                    >
                      Proceed to Checkout<ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <div className="flex items-center justify-center gap-2 text-[10px] sm:text-xs text-gray-500">
                      <Package className="w-3 h-3 sm:w-4 sm:h-4" /><span>Delivery within 5-7 business days</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Design Preview Modal */}
      <Dialog open={!!previewItem} onOpenChange={(open) => { if (!open) setPreviewItem(null); }}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-0">
            <DialogTitle className="text-base">{previewItem?.product?.name || 'Design Preview'}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-3">
            <div className="flex justify-center gap-1">
              <button onClick={() => setPreviewSide('front')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'front' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid="cart-preview-front-btn">Front</button>
              <button onClick={() => setPreviewSide('back')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'back' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid="cart-preview-back-btn">Back</button>
            </div>
            {previewItem && (
              <div className="rounded-xl overflow-hidden shadow-xl border max-w-lg mx-auto">
                <DesignCardPreview design={buildPreviewData(previewItem)} side={previewSide} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Cart;
